import { z } from 'zod';
/**
 * A recipe describes one carrier portal as data. The extension interprets the
 * `session` and `capture` sections (it must never eval code from a recipe); the
 * server interprets `mapping`. Recipes are served by the server so portal fixes
 * ship without a Chrome Web Store release.
 */
/* ---------------------------- capture (extension) ------------------------ */
const Throttle = z.object({ minMs: z.number().int().min(0), maxMs: z.number().int().min(0) });
export const ListStep = z.object({
    /** Relative URL template; `{page}` is substituted. Fetched same-origin, HTML parsed with DOMParser. */
    url: z.string(),
    startPage: z.number().int().default(1),
    maxPages: z.number().int().default(200),
    /** Elements whose attribute holds a link to a detail record. */
    linkSelector: z.string(),
    linkAttribute: z.string().default('href'),
    /** Regex with one capture group that extracts the record id from the attribute. */
    idPattern: z.string(),
    /** Optional selector for the "next page" control; if absent, paging stops on an empty or repeated page. */
    nextPageSelector: z.string().optional(),
});
export const DetailStep = z.object({
    /** Relative URL template; `{id}` is substituted. */
    url: z.string(),
    format: z.enum(['json', 'html']),
    /** For html: the element to keep (the rest of the page is dropped before upload). */
    scopeSelector: z.string().optional(),
    /** Follow-up fetches merged into the payload under `key` (e.g. deferred batches). */
    merge: z
        .array(z.object({
        key: z.string(),
        url: z.string(),
        /** Repeat with `{n}` = 1..; stop when `until` path is falsy or n > max. */
        repeat: z.object({ max: z.number().int(), untilPath: z.string() }).optional(),
    }))
        .default([]),
});
export const Recipe = z.object({
    id: z.string().regex(/^[a-z0-9-]+$/),
    version: z.number().int().min(1),
    name: z.string(),
    /** Carrier or MGA shown to the advisor. */
    vendor: z.string(),
    match: z.object({
        origins: z.array(z.string()),
        /** Recipe applies when the tab path starts with one of these… */
        pathPrefixes: z.array(z.string()).default(['/']),
        /** …and with none of these. */
        excludePathPrefixes: z.array(z.string()).default([]),
    }),
    session: z.object({
        /** Fetched to check the advisor is signed in. A redirect to `loginPathPattern` or a 401 = signed out. */
        checkUrl: z.string(),
        loginPathPattern: z.string(),
        /** Where to read the signed-in portal user's display name on the check page. */
        userSelector: z.string().optional(),
    }),
    capture: z.object({
        list: ListStep,
        detail: DetailStep,
        throttle: Throttle.default({ minMs: 600, maxMs: 1400 }),
        /** Hard stop so a broken recipe can't crawl forever. */
        maxRecords: z.number().int().default(5000),
    }),
    mapping: z.lazy(() => Mapping),
});
export const Transform = z.enum(['string', 'number', 'money', 'date', 'trim', 'upper', 'boolean']);
export const FieldSpecSchema = z.lazy(() => z.union([
    z.string(),
    z.object({ const: z.unknown() }).strict(),
    z
        .object({
        path: z.string(),
        transform: Transform.optional(),
        lookup: z.string().optional(),
        default: z.unknown().optional(),
    })
        .strict(),
    z
        .object({
        compute: z.literal('annualize'),
        amount: z.string(),
        frequency: z.string(),
        frequencyLookup: z.string().optional(),
    })
        .strict(),
    z.object({ compute: z.literal('namePart'), path: z.string(), part: z.enum(['first', 'last']) }).strict(),
    z.object({ compute: z.literal('firstOf'), paths: z.array(z.string()), transform: Transform.optional() }).strict(),
    z.object({ each: z.string(), fields: z.record(z.string(), FieldSpecSchema) }).strict(),
]));
export const Mapping = z.object({
    /** Path to the person object inside the captured payload. */
    clientPath: z.string(),
    client: z.record(z.string(), FieldSpecSchema),
    /** Path (from the payload root) to the array of policies. */
    policiesPath: z.string(),
    policy: z.record(z.string(), FieldSpecSchema),
    /** Named value maps; keys are matched case-insensitively. `*` is the fallback. */
    lookups: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
});
export function toCaptureRecipe(r) {
    const { mapping: _mapping, ...rest } = r;
    return rest;
}
//# sourceMappingURL=recipe.js.map